// Libs
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getInitials } from "@/lib/utils";

// Hooks
import { useBrandsApi } from "@/hooks/useBrandsApi";
import useDebounce from "@/hooks/useDebounce";
import { useDataFiltering } from "@/hooks/useDataFiltering";

//Components
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import BrandModal from "@/components/modals/BrandModal";
import { Input } from "@/components/atoms/input";
import { Button } from "@/components/atoms/button";
import { Card } from "@/components/atoms/card";
import OrdersTable from "@/components/orders/OrdersTable";
import { EmptyState } from "@/components/atoms/empty-state";
import Pagination from "@/components/atoms/Pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/dialog";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/atoms/avatar";

// Icons
import { Pencil, Plus, Search, Users } from "lucide-react";
import { PermissionGate } from "@/components/access-control/PermissionGate";
import { usePermissions } from "@/contexts/PermissionContext";
import { BusinessAccessDenied } from "@/components/access-control/AccessDenied";
import { useAuth } from "@/hooks/useAuth";
import { PageHeader } from "@/components/PageHeader";
import { useAuth0 } from "@auth0/auth0-react";

interface Manager {
  id: number;
  role: string;
  email: string;
  phone: string | null;
  active: boolean;
  picture: string | null;
  last_name: string;
  created_at: string;
  first_name: string;
}

interface BrandWithCounts {
  id: number;
  name: string;
  branchCount: number;
  driverCount: number;
  managers: Manager[];
  country?: {
    name: string;
    short_name: string;
    flag_url: string;
  };
}

// Helper para obtener iniciales de manager
function getManagerInitials(manager: Manager) {
  if (manager.first_name && manager.last_name) {
    return (
      (manager.first_name[0] || "") + (manager.last_name[0] || "")
    ).toUpperCase();
  }
  const firstName = manager.first_name || "";
  const lastName = manager.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName ? getInitials(fullName) : "M";
}

export const CountryBadge = ({
  country,
}: {
  country?: { name: string; short_name: string; flag_url: string };
}) => {
  if (!country) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 ml-2">
      {country.flag_url ? (
        <img
          src={country.flag_url}
          alt={country.name}
          className="h-3 w-3 xl:h-4 xl:w-4 rounded-full object-cover"
          loading="lazy"
        />
      ) : null}
      <span className="leading-none">{country.short_name || country.name}</span>
    </span>
  );
};

const ManagersAvatarStack = ({
  managers,
  brandName,
}: {
  managers: Manager[];
  brandName: string;
}) => {
  if (!managers || managers.length === 0) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  const maxVisible = 3;
  const visibleManagers = managers.slice(0, maxVisible);
  const remainingCount = Math.max(0, managers.length - maxVisible);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className="flex items-center -space-x-1.5 sm:-space-x-2 hover:scale-105 transition-transform cursor-pointer max-w-fit"
          onClick={(e) => e.stopPropagation()}
          tabIndex={0}
          type="button"
        >
          {visibleManagers.map((manager, index) => (
            <div
              key={manager.id}
              className="relative ring-2 ring-white rounded-full flex-shrink-0"
              style={{ zIndex: maxVisible - index }}
            >
              <Avatar className="h-6 w-6 sm:h-8 sm:w-8">
                {manager.picture ? (
                  <>
                    <AvatarImage
                      src={manager.picture}
                      alt={manager.first_name + " " + manager.last_name}
                    />
                    <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                      {getManagerInitials(manager)}
                    </AvatarFallback>
                  </>
                ) : (
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                    {getManagerInitials(manager)}
                  </AvatarFallback>
                )}
              </Avatar>
            </div>
          ))}
          {remainingCount > 0 && (
            <div className="relative ring-2 ring-white rounded-full bg-gray-100 h-6 w-6 sm:h-8 sm:w-8 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-gray-600">
                +{remainingCount}
              </span>
            </div>
          )}
        </button>
      </DialogTrigger>

      <DialogContent
        className="sm:max-w-md bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Encargados de {brandName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {managers.length === 0 ? (
            <div className="text-center py-6">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">No hay encargados asignados</p>
            </div>
          ) : (
            <div className="space-y-3">
              {managers.map((manager) => (
                <div
                  key={manager.id}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Avatar className="h-10 w-10">
                    {manager.picture ? (
                      <>
                        <AvatarImage
                          src={manager.picture}
                          alt={manager.first_name + " " + manager.last_name}
                        />
                        <AvatarFallback className="bg-indigo-100 text-indigo-600">
                          {getManagerInitials(manager)}
                        </AvatarFallback>
                      </>
                    ) : (
                      <AvatarFallback className="bg-indigo-100 text-indigo-600">
                        {getManagerInitials(manager)}
                      </AvatarFallback>
                    )}
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {manager.first_name + " " + manager.last_name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {manager.role} - {manager.email}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Brand() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500); // 500ms delay
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loadingFilteredBrands, setLoadingFilteredBrands] = useState(false);
  const {
    brands,
    totalCount,
    loading,
    error,
    fetchBrandsPaginated,
    createBrand,
  } = useBrandsApi();
  const navigate = useNavigate();
  const { hasPermission, resourceAccess } = usePermissions();
  const { filterBrands, getBrandFilter } = useDataFiltering();
  const { userRole } = useAuth();
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const { isManager } = useAuth();
  const { user } = useAuth0();

  // Access control
  const canViewBrands = hasPermission("business:read");
  const hasAccessibleBrands =
    resourceAccess.canAccessAll || resourceAccess.brandIds.length > 0;
  const shouldShowAccessDenied =
    !canViewBrands || (userRole === "manager" && !hasAccessibleBrands);

  // Derived flags
  const isSearching = (debouncedSearch || "").trim() !== "";
  const isInitialLoading = loading && brands.length === 0;
  const isContentLoading =
    loadingFilteredBrands && brands.length > 0 && !isSearching;

  // Single source of processed brands (no setState)
  const filteredBrands: BrandWithCounts[] = useMemo(() => {
    if (!brands || brands.length === 0) return [];
    const accessible = filterBrands(brands) || [];
    const processed = accessible.map((brand: any) => {
      const branchIds = Array.isArray(brand.branches)
        ? brand.branches.map((b: any) => b.id)
        : [];
      let driverCount = 0;
      if (Array.isArray(brand.branches)) {
        brand.branches.forEach((branch: any) => {
          if (Array.isArray(branch.drivers))
            driverCount += branch.drivers.length;
        });
      }
      const managers: Manager[] = Array.isArray(brand.managers)
        ? brand.managers
        : [];
      return {
        id: brand.id,
        name: brand.name,
        branchCount: branchIds.length,
        driverCount,
        managers,
        country: brand.country
          ? {
              name: brand.country.name,
              short_name: brand.country.short_name,
              flag_url: brand.country.flag_url,
            }
          : undefined,
      } as BrandWithCounts;
    });
    return processed.sort((a, b) => a.id - b.id);
  }, [brands]);

  // Fetch logic:
  // 1) When searching (debouncedSearch changes) always fetch page 1 with search
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const brandFilter = getBrandFilter();
      setLoadingFilteredBrands(true);
      try {
        await fetchBrandsPaginated({
          page: 1,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          brandIds: brandFilter,
          user: isManager ? user : undefined,
        });
        if (!cancelled) setCurrentPage(1);
      } finally {
        if (!cancelled) setLoadingFilteredBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, isManager, user]);

  // 2) When not searching, fetch currentPage
  useEffect(() => {
    if (isSearching) return; // avoid duplicate fetch while searching
    let cancelled = false;
    (async () => {
      const brandFilter = getBrandFilter();
      setLoadingFilteredBrands(true);
      try {
        await fetchBrandsPaginated({
          page: currentPage,
          limit: PAGE_SIZE,
          search: undefined,
          brandIds: brandFilter,
          user: isManager ? user : undefined,
        });
      } finally {
        if (!cancelled) setLoadingFilteredBrands(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentPage, isSearching, isManager, user]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSaveBrand = async (brandData: {
    name: string;
    countryId?: number;
    groupingDistanceMeters?: number;
  }) => {
    try {
      await createBrand(brandData);
      setIsModalOpen(false);
    } catch {
      // hook handles toasts/errors
    }
  };

  const handleViewBrandDetails = (brandId: number, editable = false) => {
    if (editable) navigate(`/brands/${brandId}?edit=1`);
    else navigate(`/brands/${brandId}`);
  };

  if (error) return <ErrorMessage message={error} />;

  if (shouldShowAccessDenied) return <BusinessAccessDenied />;

  const columns = [
    { header: "ID", key: "id" },
    { header: "Nombre", key: "nombre" },
    { header: "Sucursales", key: "sucursales" },
    { header: "Conductores", key: "conductores" },
    { header: "Encargados", key: "encargados" },
    { header: "Acciones", key: "acciones" },
  ];

  // Single loading UI state (no duplicated components/messages)
  if (isInitialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 w-full">
        <div className="flex flex-col items-center gap-3">
          <LoadingSpinner height="h-auto" />
          <div className="text-sm text-gray-600 ">
            {isSearching ? "Buscando negocios..." : "Cargando negocios..."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 w-full">
      <PageHeader
        title="Negocios"
        description="Gestión de negocios y sucursales"
      />

      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <Card className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 gap-3">
              <div className="flex-1 max-w-md">
                <Input
                  className="w-full h-10"
                  placeholder="Buscar negocio..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  icon={<Search className="w-4 h-4" strokeWidth={2} />}
                />
              </div>
              <PermissionGate permission="business:create">
                <Button
                  onClick={handleOpenModal}
                  className="w-full sm:w-auto px-4 py-2 h-10 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" strokeWidth={2} />
                  <span>Nuevo negocio</span>
                </Button>
              </PermissionGate>
            </div>
          </div>

          <div className="min-h-[500px]">
            {isContentLoading ? (
              <div className="flex items-center justify-center h-[300px]">
                <div className="flex flex-col items-center gap-2">
                  <LoadingSpinner />
                  <div className="text-sm text-gray-600">
                    {isSearching
                      ? "Buscando negocios..."
                      : "Actualizando lista..."}
                  </div>
                </div>
              </div>
            ) : loadingFilteredBrands ? (
              <div className="p-6">
                <EmptyState
                  title={
                    isSearching
                      ? "No se encontraron negocios"
                      : "No hay negocios disponibles"
                  }
                  description={
                    isSearching
                      ? "Intenta con otro término o limpia el filtro."
                      : "Agrega un nuevo negocio para empezar"
                  }
                  action={{
                    label: "Nuevo negocio",
                    onClick: handleOpenModal,
                    disabled: !hasPermission("business:create"),
                  }}
                />
              </div>
            ) : (
              <>
                {/* Desktop */}
                <div className="hidden xl:block">
                  <div className="overflow-x-auto">
                    <OrdersTable
                      columns={columns}
                      emptyMessage="No hay negocios disponibles"
                    >
                      {filteredBrands.map((brand) => {
                        const initials = getInitials(brand.name || "NA");
                        const { branchCount, driverCount, managers } = brand;
                        return (
                          <tr
                            key={brand.id}
                            className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                            onClick={(e) => {
                              if (
                                (e.target as HTMLElement).closest(
                                  ".managers-avatar-stack",
                                )
                              )
                                return;
                              handleViewBrandDetails(brand.id);
                            }}
                          >
                            <td className="py-4 pl-6 text-sm font-medium text-gray-900">
                              #{brand.id}
                            </td>
                            <td className="py-4 pl-6">
                              <div className="flex items-center gap-1">
                                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-semibold">
                                  {initials}
                                </div>
                                <span className="font-medium text-gray-900 text-sm">
                                  {brand.name}
                                </span>
                                <CountryBadge country={brand.country} />
                              </div>
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-700">
                              {branchCount}
                            </td>
                            <td className="py-4 px-6 text-sm text-gray-700">
                              {driverCount}
                            </td>
                            <td className="py-4 px-6">
                              <span className="managers-avatar-stack">
                                <ManagersAvatarStack
                                  managers={managers}
                                  brandName={brand.name}
                                />
                              </span>
                            </td>
                            <td className="py-4 px-6">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700 rounded-md transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewBrandDetails(brand.id, true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </OrdersTable>
                  </div>
                </div>

                {/* Tablet */}
                <div className="hidden lg:block xl:hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Negocio
                          </th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Estadísticas
                          </th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredBrands.map((brand) => {
                          const initials = getInitials(brand.name || "NA");
                          const { branchCount, driverCount, managers } = brand;
                          return (
                            <tr
                              key={brand.id}
                              className="hover:bg-gray-50 cursor-pointer transition-colors"
                              onClick={(e) => {
                                if (
                                  (e.target as HTMLElement).closest(
                                    ".managers-avatar-stack",
                                  )
                                )
                                  return;
                                handleViewBrandDetails(brand.id);
                              }}
                            >
                              <td className="py-4 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold">
                                    {initials}
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900 flex items-center flex-wrap gap-2">
                                      <span>{brand.name}</span>
                                    </div>
                                    <div className="text-sm flex items-center gap-1 text-gray-500">
                                      <span>#{brand.id}</span>
                                      <CountryBadge country={brand.country} />
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4">
                                <div className="flex gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">
                                      Sucursales:{" "}
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {branchCount}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">
                                      Conductores:{" "}
                                    </span>
                                    <span className="font-medium text-gray-900">
                                      {driverCount}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">
                                      Encargados:{" "}
                                    </span>
                                    <span className="managers-avatar-stack">
                                      <ManagersAvatarStack
                                        managers={managers}
                                        brandName={brand.name}
                                      />
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="py-4 px-4 text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-2 text-gray-500 hover:bg-gray-200 hover:text-gray-700 rounded-md transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewBrandDetails(brand.id, true);
                                  }}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile */}
                <div className="lg:hidden">
                  <div className="p-2 sm:p-4 space-y-3">
                    {filteredBrands.map((brand) => {
                      const initials = getInitials(brand.name || "NA");
                      const { branchCount, driverCount, managers } = brand;
                      return (
                        <Card
                          key={brand.id}
                          className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                          onClick={(e) => {
                            if (
                              (e.target as HTMLElement).closest(
                                ".managers-avatar-stack",
                              )
                            )
                              return;
                            handleViewBrandDetails(brand.id);
                          }}
                        >
                          <div className="p-3 sm:p-4 pb-2 sm:pb-3">
                            <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2">
                              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
                                  {initials}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h3 className="font-semibold text-gray-900 text-base break-words leading-tight flex items-center flex-wrap gap-1 sm:gap-2">
                                    <span className="truncate max-w-[120px] sm:max-w-none">
                                      {brand.name}
                                    </span>
                                    <CountryBadge country={brand.country} />
                                  </h3>
                                  <p className="text-xs sm:text-sm text-gray-500">
                                    #{brand.id}
                                  </p>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded-lg transition-colors flex-shrink-0"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewBrandDetails(brand.id, true);
                                }}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="px-3 sm:px-4 pb-2 sm:pb-3">
                            <div className="grid grid-cols-2 gap-2 sm:gap-4 mb-2 sm:mb-3">
                              <div className="text-center">
                                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                                  {branchCount}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 sm:mt-1">
                                  Sucursales
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-lg sm:text-xl font-semibold text-gray-900">
                                  {driverCount}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 sm:mt-1">
                                  Conductores
                                </div>
                              </div>
                            </div>

                            <div className="pt-2 sm:pt-3 border-t border-gray-100">
                              <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-1">
                                <span className="text-xs text-gray-500">
                                  Encargados
                                </span>
                                <div className="managers-avatar-stack mt-1 xs:mt-0">
                                  <ManagersAvatarStack
                                    managers={managers}
                                    brandName={brand.name}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-4 border-t border-gray-200">
                  <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                  {totalCount > 0 && (
                    <div className="text-sm text-gray-500 mt-3 text-center sm:text-left">
                      Mostrando página {currentPage} de {totalPages} (
                      {totalCount} negocios en total)
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      <BrandModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveBrand}
      />
    </div>
  );
}
